import styles from './searchable-dropdown.module.css'

import { ChevronDownIcon, Portal } from '@radix-ui/themes'
import type { ReactNode } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { Box, Spinner, Text, TextField } from 'components/ds'

import useEventListener from 'hooks/use-event-listener'

/**
 * Option interface for dropdown items
 */
export interface SearchableDropdownOption<T = unknown> {
  /** Unique identifier for the option */
  id: string
  /** Display label for the option */
  label: string
  /** Optional additional data */
  data?: T
}

/**
 * Group interface for organizing options
 */
export interface SearchableDropdownGroup<T = unknown> {
  /** Group label/header */
  label: string
  /** Options within the group */
  options: SearchableDropdownOption<T>[]
}

/**
 * Props for the SearchableDropdown component
 */
export interface SearchableDropdownProps<T = unknown> {
  /** Current value (selected option ID) */
  value?: string
  /** Callback when an option is selected */
  onSelect: (option: SearchableDropdownOption<T>) => void
  /** Callback when search input changes (deprecated - use onSearch instead) */
  onSearchChange?: (searchValue: string) => void
  /** Async search function that returns options based on query */
  onSearch?: (
    query: string,
  ) => Promise<SearchableDropdownOption<T>[]> | SearchableDropdownOption<T>[]
  /** Array of options or groups (required if onSearch is not provided) */
  options?: SearchableDropdownOption<T>[] | SearchableDropdownGroup<T>[]
  /** Whether data is currently loading */
  loading?: boolean
  /** Placeholder text for the search input */
  placeholder?: string
  /** Custom renderer for options */
  renderOption?: (option: SearchableDropdownOption<T>) => ReactNode
  /** Optional action component renderer function that receives the current search value */
  actionComponent?: (searchValue: string) => ReactNode
  /** Callback when action component is clicked */
  onActionClick?: () => void
  /** Optional ARIA label for accessibility */
  'aria-label'?: string
  /** Whether the dropdown is disabled */
  disabled?: boolean
  /** Debounce delay for search in milliseconds (default: 300) */
  searchDebounce?: number
  /** Max height of dropdown in pixels (default: 180) */
  maxHeight?: number
  /** Callback when the dropdown loses focus */
  onBlur?: () => void
  /** Custom dropdown width in px (defaults to input width) */
  customDropdownWidth?: number
}

/**
 * Type guard to check if options array contains groups
 */
export function isGroupedOptions<T>(
  options: SearchableDropdownOption<T>[] | SearchableDropdownGroup<T>[],
): options is SearchableDropdownGroup<T>[] {
  return options.length > 0 && 'options' in options[0]
}

/**
 * SearchableDropdown - A reusable searchable dropdown component with grouped results
 *
 * This component provides a flexible dropdown interface with search functionality,
 * keyboard navigation, and support for grouped results. It's designed to be
 * reusable across different contexts (writers, publishers, PROs, etc.).
 *
 * @note This component aims to replace the CustomDropdown component
 * (components/ui/dropdowns/custom-dropdown) which is built on an outdated version
 * of react-select. New features should use SearchableDropdown instead.
 *
 * Features:
 * - Searchable input field with debounced search
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Grouped results with section headers
 * - Custom option rendering
 * - Optional action component (e.g., "Create new" button)
 * - Loading states
 * - Full accessibility support with ARIA attributes
 * - Click-outside to close
 *
 * Keyboard shortcuts:
 * - Enter: Select highlighted option or trigger action component
 * - Escape: Close dropdown
 * - Tab: Select highlighted option or trigger action component, then move focus to next element
 * - Shift+Tab: Close dropdown and move focus to previous element
 * - ArrowDown: Navigate to next option or action component
 * - ArrowUp: Navigate to previous option or back from action component
 *
 * @param props - Component props
 * @returns SearchableDropdown component
 *
 * @example
 * ```tsx
 * // Simple usage with client-side filtering
 * <SearchableDropdown
 *   value={selectedId}
 *   onSelect={(option) => handleSelect(option)}
 *   options={[
 *     { id: '1', label: 'Option 1' },
 *     { id: '2', label: 'Option 2' },
 *   ]}
 *   placeholder="Select an option..."
 * />
 *
 * // With action component (function form to access search value)
 * <SearchableDropdown
 *   value={selectedId}
 *   onSelect={(option) => handleSelect(option)}
 *   options={options}
 *   actionComponent={(searchValue) => (
 *     <CreateNewAction
 *       label="Create new item"
 *       to="create"
 *       searchText={searchValue}
 *     />
 *   )}
 * />
 *
 * // With server-side search
 * <SearchableDropdown
 *   value={selectedId}
 *   onSelect={(option) => handleSelect(option)}
 *   options={searchResults}
 *   onSearchChange={(search) => handleServerSearch(search)}
 *   actionComponent={(searchValue) => (
 *     <CreateNewAction label="Create new" searchText={searchValue} />
 *   )}
 * />
 * ```
 */
export function SearchableDropdown<T = unknown>({
  value,
  onSelect,
  onSearchChange,
  onSearch,
  options: optionsProp = [],
  loading: loadingProp = false,
  placeholder = 'Search...',
  renderOption,
  actionComponent,
  onActionClick,
  'aria-label': ariaLabel = 'Searchable dropdown',
  disabled = false,
  searchDebounce = 300,
  maxHeight = 180,
  onBlur,
  customDropdownWidth,
}: SearchableDropdownProps<T>) {
  const [searchValue, setSearchValue] = useState('')
  // Track the actual user-typed search text separately from searchValue
  // searchValue may be pre-populated with selected label for display,
  // but userSearchInput only contains what the user has actually typed
  const [userSearchInput, setUserSearchInput] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [isActionFocused, setIsActionFocused] = useState(false)
  const [hasNavigated, setHasNavigated] = useState(false)
  const [internalOptions, setInternalOptions] = useState<
    SearchableDropdownOption<T>[] | SearchableDropdownGroup<T>[]
  >(optionsProp)
  const [internalLoading, setInternalLoading] = useState(false)
  const [selectedOption, setSelectedOption] =
    useState<SearchableDropdownOption<T> | null>(null)
  const [portalPosition, setPortalPosition] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const actionRef = useRef<HTMLDivElement>(null)

  // Use internal options/loading if onSearch is provided, otherwise use props
  const options = onSearch ? internalOptions : optionsProp
  const loading = onSearch ? internalLoading : loadingProp

  // Flatten all options (unfiltered) and ensure selectedOption is included when needed
  const allOptions = useMemo(() => {
    let opts: SearchableDropdownOption<T>[]
    if (isGroupedOptions(options)) {
      opts = options.flatMap(group => group.options)
    } else {
      opts = options
    }

    // If using onSearch mode and we have a selectedOption that's not in the results
    // Only add it when dropdown is closed (to show the selected label)
    // Don't add it during active search to avoid duplicates
    if (
      onSearch &&
      selectedOption &&
      !isOpen &&
      !opts.some(opt => opt.id === selectedOption.id)
    ) {
      return [selectedOption, ...opts]
    }

    return opts
  }, [options, onSearch, selectedOption, isOpen])

  // Apply client-side filtering for display
  const flattenedOptions = useMemo(() => {
    // If onSearchChange is provided, parent handles filtering - return all options
    if (onSearchChange) {
      return allOptions
    }

    // Otherwise, apply client-side filtering based on search value
    if (!searchValue.trim()) {
      return allOptions
    }

    const lowerSearch = searchValue.toLowerCase()
    return allOptions.filter(opt =>
      opt.label.toLowerCase().includes(lowerSearch),
    )
  }, [allOptions, searchValue, onSearchChange])

  // Find selected option label from ALL options, not filtered ones
  const selectedLabel = useMemo(() => {
    if (!value) return ''
    // First check selectedOption if it matches the value
    if (selectedOption && selectedOption.id === value) {
      return selectedOption.label
    }
    // Otherwise search in allOptions
    const option = allOptions.find(opt => opt.id === value)
    return option?.label || ''
  }, [value, allOptions, selectedOption])

  // Derive loading state for when value is passed but not yet resolved
  const isValueLoading = Boolean(value && !selectedLabel && loading)

  // Async search handler
  const performSearch = useCallback(
    async (query: string) => {
      if (onSearch) {
        setInternalLoading(true)
        try {
          const results = await onSearch(query)
          setInternalOptions(results)
        } catch (error) {
          console.error('Search failed:', error)
          setInternalOptions([])
        } finally {
          setInternalLoading(false)
        }
      }
    },
    [onSearch],
  )

  // Debounced search callback
  const debouncedSearch = useDebouncedCallback((value: string) => {
    if (onSearchChange) {
      onSearchChange(value)
    } else if (onSearch) {
      performSearch(value)
    }
  }, searchDebounce)

  // Perform search when dropdown opens (only for onSearch mode)
  useEffect(() => {
    if (onSearch && isOpen) {
      // If searchValue is empty, perform initial search with empty query
      if (!searchValue) {
        performSearch('')
      } else if (searchValue === selectedLabel && selectedLabel) {
        // If searchValue is the selected label, search for that to show matching results
        performSearch(selectedLabel)
      }
    }
  }, [isOpen, onSearch, searchValue, selectedLabel, performSearch])

  // Track selected option when value changes or when allOptions updates
  useEffect(() => {
    if (!value) {
      setSelectedOption(null)
      return
    }

    // Always look for the option in allOptions to get fresh data
    const option = allOptions.find(opt => opt.id === value)
    if (option) {
      // Only update if the option data has actually changed (to avoid unnecessary re-renders)
      if (
        !selectedOption ||
        selectedOption.id !== option.id ||
        selectedOption.label !== option.label
      ) {
        setSelectedOption(option)
      }
    }
  }, [value, allOptions, selectedOption])

  // Calculate portal position from container ref
  const updatePortalPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setPortalPosition({
        top: rect.bottom + 4, // 4px gap
        left: rect.left,
        width: customDropdownWidth ?? rect.width,
      })
    }
  }, [customDropdownWidth])

  // Update portal position when dropdown opens
  useEffect(() => {
    if (isOpen) {
      updatePortalPosition()
    }
  }, [isOpen, updatePortalPosition])

  // Reposition dropdown on viewport resize and scroll
  useEffect(() => {
    if (!isOpen) return

    const handleReposition = () => {
      updatePortalPosition()
    }

    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)

    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
    }
  }, [isOpen, updatePortalPosition])

  // Find the Radix Themes container for portal
  const getPortalContainer = useCallback(() => {
    // Find the closest .radix-themes ancestor to ensure theme styles are applied
    return (
      (containerRef.current?.closest('.radix-themes') as HTMLElement | null) ??
      undefined
    )
  }, [])

  // Sync searchValue with selectedLabel when dropdown is closed
  useEffect(() => {
    if (!isOpen) {
      setSearchValue(selectedLabel)
    }
  }, [isOpen, selectedLabel])

  // Reset selection when options change - prefer currently selected value's index
  useEffect(() => {
    if (flattenedOptions.length > 0 && selectedIndex === -1 && isOpen) {
      // Find index of currently selected value, fallback to 0
      const currentValueIndex = value
        ? flattenedOptions.findIndex(opt => opt.id === value)
        : -1
      setSelectedIndex(currentValueIndex >= 0 ? currentValueIndex : 0)
    } else if (flattenedOptions.length === 0) {
      setSelectedIndex(-1)
    }
  }, [flattenedOptions.length, selectedIndex, isOpen, value, flattenedOptions])

  // Handle clicks outside to close dropdown
  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node
    const isInsideContainer = containerRef.current?.contains(target)
    const isInsideDropdown = dropdownRef.current?.contains(target)
    const isInsideAction = actionRef.current?.contains(target)

    if (!isInsideContainer && !isInsideDropdown && !isInsideAction) {
      setIsOpen(false)
      setSelectedIndex(-1)
      setIsActionFocused(false)
    }
  }, [])

  useEventListener('mousedown', handleClickOutside)

  // Handle option selection
  const handleSelect = useCallback(
    (option: SearchableDropdownOption<T>) => {
      setSelectedOption(option)
      onSelect(option)
      setIsOpen(false)
      setSearchValue(option.label)
      setSelectedIndex(-1)
      setIsActionFocused(false)
    },
    [onSelect],
  )

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen) {
        if (e.key === 'Enter' || e.key === 'ArrowDown') {
          e.preventDefault()
          setIsOpen(true)
          setSelectedIndex(0)
        }
        return
      }

      if (e.key === 'Tab') {
        // Shift+Tab should not select anything
        if (e.shiftKey) {
          // If search is empty and there was a previous value, reset it
          if (!searchValue.trim() && selectedLabel) {
            onSelect(undefined as any)
            setSearchValue('')
          } else {
            // Otherwise restore original value
            setSearchValue(selectedLabel || '')
          }
          setIsOpen(false)
          setSelectedIndex(-1)
          setIsActionFocused(false)
          setHasNavigated(false)
          // Don't prevent default - let Shift+Tab move focus backwards
          return
        }

        // If action component is focused, trigger it like Enter does
        if (isActionFocused) {
          e.preventDefault() // Prevent Tab from moving focus immediately
          // Trigger action component click
          const actionElement = actionRef.current?.querySelector(
            'button, a, [role="button"], [onclick], div[onclick]',
          ) as HTMLElement
          if (actionElement) {
            actionElement.click()
          } else if (actionRef.current?.firstElementChild) {
            // If no clickable element found, try clicking the first child
            ;(actionRef.current.firstElementChild as HTMLElement).click()
          }
          setHasNavigated(false)
          return
        }
        // If search input is empty, user hasn't navigated, AND there was a previously selected value, reset
        if (!searchValue.trim() && !hasNavigated && selectedLabel) {
          onSelect(undefined as any)
          setSearchValue('')
          setIsOpen(false)
          setSelectedIndex(-1)
          setIsActionFocused(false)
          setHasNavigated(false)
          // Don't prevent default - let Tab move focus naturally
          return
        }
        // If an option is selected (not action component), select it like Enter
        if (
          !isActionFocused &&
          selectedIndex >= 0 &&
          selectedIndex < flattenedOptions.length
        ) {
          handleSelect(flattenedOptions[selectedIndex])
          setHasNavigated(false)
          // Don't prevent default - let Tab move focus after selection
          return
        }
        // Otherwise, restore original value and close dropdown
        setSearchValue(selectedLabel || '')
        setIsOpen(false)
        setSelectedIndex(-1)
        setIsActionFocused(false)
        setHasNavigated(false)
        // Don't prevent default - let Tab move focus naturally
        return
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHasNavigated(true)
        if (isActionFocused) {
          // Already at the bottom (action component)
          return
        }
        if (selectedIndex < flattenedOptions.length - 1) {
          // Navigate to next option
          setSelectedIndex(prev => prev + 1)
        } else if (
          actionComponent &&
          selectedIndex === flattenedOptions.length - 1
        ) {
          // Navigate to action component only from last option
          setIsActionFocused(true)
          setSelectedIndex(-1)
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHasNavigated(true)
        if (isActionFocused) {
          // Navigate back from action to last option
          setIsActionFocused(false)
          setSelectedIndex(flattenedOptions.length - 1)
        } else if (selectedIndex > 0) {
          // Navigate to previous option
          setSelectedIndex(prev => prev - 1)
        } else {
          // Already at the top
          setSelectedIndex(0)
        }
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (isActionFocused) {
          // Trigger action component click
          const actionElement = actionRef.current?.querySelector(
            'button, a, [role="button"], [onclick], div[onclick]',
          ) as HTMLElement
          if (actionElement) {
            actionElement.click()
          } else if (actionRef.current?.firstElementChild) {
            // If no clickable element found, try clicking the first child
            ;(actionRef.current.firstElementChild as HTMLElement).click()
          }
        } else if (
          selectedIndex >= 0 &&
          selectedIndex < flattenedOptions.length
        ) {
          handleSelect(flattenedOptions[selectedIndex])
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        setIsOpen(false)
        setSearchValue('')
        setSelectedIndex(-1)
        setIsActionFocused(false)
        setHasNavigated(false)
      }
    },
    [
      isOpen,
      selectedIndex,
      isActionFocused,
      flattenedOptions,
      handleSelect,
      actionComponent,
      searchValue,
      onSelect,
      selectedLabel,
      hasNavigated,
    ],
  )

  // Auto-scroll selected option into view
  useEffect(() => {
    if (!isOpen) return

    if (isActionFocused && actionRef.current) {
      // Scroll action component into view
      if (actionRef.current.scrollIntoView) {
        actionRef.current.scrollIntoView({
          block: 'nearest',
          behavior: 'instant',
        })
      }
    } else if (selectedIndex >= 0 && dropdownRef.current) {
      // Scroll selected option into view
      const selectedElement = dropdownRef.current.querySelector(
        `[data-option-index="${selectedIndex}"]`,
      ) as HTMLElement

      if (selectedElement && selectedElement.scrollIntoView) {
        selectedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'instant',
        })
      }
    }
  }, [selectedIndex, isActionFocused, isOpen])

  // Handle input focus
  const handleFocus = useCallback(() => {
    setIsOpen(true)
    // Pre-populate search with current selected label so user can edit it
    if (selectedLabel) {
      setSearchValue(selectedLabel)
      // Select all text to allow quick deletion/replacement
      setTimeout(() => {
        inputRef.current?.select()
      }, 0)
    }
    // Reset userSearchInput when opening the dropdown
    // User hasn't typed anything yet at this point
    setUserSearchInput('')
  }, [selectedLabel])

  // Handle input blur
  const handleBlur = useCallback(() => {
    // If search input is empty AND there was a previously selected value, reset it
    if (!searchValue.trim() && selectedLabel) {
      onSelect(undefined as any)
      setSearchValue('')
    } else if (!searchValue.trim()) {
      // Search is empty but there was no previous value - do nothing (no change)
      setSearchValue('')
    } else {
      // If search has text but no option was selected, restore original selected label
      setSearchValue(selectedLabel || '')
    }

    // Always clear the search query for the parent when dropdown closes
    // This ensures the next time it opens, it shows all results
    if (onSearchChange) {
      onSearchChange('')
    }

    // Close dropdown when focus is lost
    setIsOpen(false)
    setSelectedIndex(-1)
    setIsActionFocused(false)
    setHasNavigated(false)

    // Call onBlur callback for validation
    onBlur?.()
  }, [searchValue, onSelect, selectedLabel, onSearchChange, onBlur])

  // Handle input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setSearchValue(value)
      // Track that the user has typed something - this is the actual user input
      setUserSearchInput(value)
      setSelectedIndex(-1)
      setIsActionFocused(false)
      setHasNavigated(false)
      // Reopen dropdown when user starts typing
      if (!isOpen) {
        setIsOpen(true)
      }
      debouncedSearch(value)
    },
    [debouncedSearch, isOpen],
  )

  // Render default option
  const renderDefaultOption = useCallback(
    (option: SearchableDropdownOption<T>) => {
      return <Text size="2">{option.label}</Text>
    },
    [],
  )

  const optionRenderer = renderOption || renderDefaultOption

  // Render grouped options
  const renderGroupedOptions = () => {
    if (!isGroupedOptions(options)) return null

    let globalIndex = 0
    const renderedGroups: JSX.Element[] = []

    options.forEach((group, groupIndex) => {
      // Filter group options to only show those in flattenedOptions
      const filteredGroupOptions = group.options.filter(option =>
        flattenedOptions.some(fo => fo.id === option.id),
      )

      // Skip rendering empty groups
      if (filteredGroupOptions.length === 0) return

      // Add separator before this group if it's not the first group and has no label
      if (groupIndex > 0 && !group.label) {
        renderedGroups.push(
          <Box
            key={`separator-${groupIndex}`}
            className={styles.actionSeparator}
          />,
        )
      }

      renderedGroups.push(
        <Box key={`group-${groupIndex}`} className={styles.group}>
          {group.label && (
            <Box px="2" py="1" className={styles.groupLabel}>
              <Text size="1" weight="medium" color="gray">
                {group.label.toUpperCase()}
              </Text>
            </Box>
          )}
          <Box className={styles.groupOptions}>
            {filteredGroupOptions.map(option => {
              const currentIndex = globalIndex++
              return (
                <Box
                  key={option.id}
                  data-option-index={currentIndex}
                  p="2"
                  className={`${styles.option} ${
                    !isActionFocused && selectedIndex === currentIndex
                      ? styles.optionSelected
                      : ''
                  }`}
                  onMouseDown={e => {
                    e.preventDefault() // Prevent blur from firing
                  }}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => {
                    setSelectedIndex(currentIndex)
                    setIsActionFocused(false)
                  }}
                >
                  {optionRenderer(option)}
                </Box>
              )
            })}
          </Box>
        </Box>,
      )
    })

    return <>{renderedGroups}</>
  }

  // Render flat options
  const renderFlatOptions = () => {
    if (isGroupedOptions(options)) return null

    return flattenedOptions.map((option, index) => (
      <Box
        key={option.id}
        data-option-index={index}
        p="2"
        className={`${styles.option} ${
          !isActionFocused && selectedIndex === index
            ? styles.optionSelected
            : ''
        }`}
        onMouseDown={e => {
          e.preventDefault() // Prevent blur from firing
        }}
        onClick={() => handleSelect(option)}
        onMouseEnter={() => {
          setSelectedIndex(index)
          setIsActionFocused(false)
        }}
      >
        {optionRenderer(option)}
      </Box>
    ))
  }

  return (
    <Box ref={containerRef} className={styles.container}>
      <TextField.Root
        ref={inputRef}
        value={searchValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-controls="searchable-dropdown-list"
        role="combobox"
        className={styles.input}
      >
        <TextField.Slot side="right">
          {isValueLoading ? (
            <Spinner size="1" />
          ) : (
            <ChevronDownIcon className={styles.chevron} />
          )}
        </TextField.Slot>
      </TextField.Root>

      {isOpen && (
        <Portal container={getPortalContainer()}>
          <Box
            className={styles.dropdownContainerPortal}
            style={
              portalPosition
                ? {
                    top: portalPosition.top,
                    left: portalPosition.left,
                    width: portalPosition.width,
                  }
                : undefined
            }
          >
            <Box
              id="searchable-dropdown-list"
              ref={dropdownRef}
              role="listbox"
              className={styles.dropdown}
              style={{ maxHeight: `${maxHeight}px` }}
            >
              {loading ? (
                <Box p="2">
                  <Text size="2" color="gray">
                    Loading...
                  </Text>
                </Box>
              ) : flattenedOptions.length === 0 ? (
                <Box p="2">
                  <Text size="2" color="gray">
                    No results found
                  </Text>
                </Box>
              ) : (
                <>
                  {isGroupedOptions(options)
                    ? renderGroupedOptions()
                    : renderFlatOptions()}
                </>
              )}
            </Box>

            {actionComponent && (
              <>
                <Box className={styles.actionSeparator} />
                <Box
                  ref={actionRef}
                  p="2"
                  className={
                    isActionFocused
                      ? styles.actionComponentFocused
                      : styles.actionComponent
                  }
                  onMouseDown={e => {
                    e.preventDefault() // Prevent blur from firing
                  }}
                  onMouseEnter={() => {
                    setIsActionFocused(true)
                    setSelectedIndex(-1)
                  }}
                  onClick={onActionClick}
                >
                  {/* Pass userSearchInput instead of searchValue to prevent
                      pre-populating Create New dialogs with the selected value's label.
                      userSearchInput only contains what the user has actually typed. */}
                  {actionComponent(userSearchInput)}
                </Box>
              </>
            )}
          </Box>
        </Portal>
      )}
    </Box>
  )
}

export default SearchableDropdown
